'use client';

import { useState, useEffect } from 'react';
import { ComplianceRequirement, ChecklistItem, RiskLevel } from '@/lib/eu-ai-act-types';
import { HIGH_RISK_REQUIREMENTS, LIMITED_RISK_REQUIREMENTS } from '@/lib/eu-ai-act-requirements';

interface Props {
  riskLevels: RiskLevel[];
  onBack: () => void;
}

type ItemStatus = 'not-started' | 'in-progress' | 'completed' | 'not-applicable';

interface ChecklistState {
  [itemId: string]: ItemStatus;
}

const CHECKLIST_STORAGE_KEY = 'eu-ai-act-checklist';

export default function EUAIActChecklist({ riskLevels, onBack }: Props) {
  const [checklistState, setChecklistState] = useState<ChecklistState>(() => {
    if (typeof window === 'undefined') return {};
    try {
      const saved = localStorage.getItem(CHECKLIST_STORAGE_KEY);
      return saved ? JSON.parse(saved) : {};
    } catch {
      return {};
    }
  });
  const [filterCategory, setFilterCategory] = useState<string>('all');
  const [filterStatus, setFilterStatus] = useState<string>('all');

  // Get applicable requirements
  const requirements: ComplianceRequirement[] = [];
  if (riskLevels.includes('high')) {
    requirements.push(...HIGH_RISK_REQUIREMENTS);
  }
  if (riskLevels.includes('limited')) {
    requirements.push(...LIMITED_RISK_REQUIREMENTS);
  }

  // Get unique categories
  const categories = [...new Set(requirements.map((r) => r.category))];

  // Save state on changes
  useEffect(() => {
    localStorage.setItem(CHECKLIST_STORAGE_KEY, JSON.stringify(checklistState));
  }, [checklistState]);

  const updateItemStatus = (itemId: string, status: ItemStatus) => {
    setChecklistState({ ...checklistState, [itemId]: status });
  };

  // Calculate stats
  const allItems = requirements.flatMap((r) => r.checklistItems);
  const totalItems = allItems.length;
  const completedItems = allItems.filter(
    (item) => checklistState[item.id] === 'completed'
  ).length;
  const inProgressItems = allItems.filter(
    (item) => checklistState[item.id] === 'in-progress'
  ).length;
  const completionPercentage = totalItems > 0 ? Math.round((completedItems / totalItems) * 100) : 0;

  // Filter requirements
  const filteredRequirements = requirements.filter((req) => {
    if (filterCategory !== 'all' && req.category !== filterCategory) return false;
    if (filterStatus !== 'all') {
      const hasMatchingStatus = req.checklistItems.some(
        (item) => (checklistState[item.id] || 'not-started') === filterStatus
      );
      if (!hasMatchingStatus) return false;
    }
    return true;
  });

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <button
            onClick={onBack}
            className="text-accent hover:underline text-sm mb-2 flex items-center gap-1"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            Back to Results
          </button>
          <h1 className="text-2xl font-bold text-light-text dark:text-foreground">
            EU AI Act Compliance Checklist
          </h1>
        </div>
      </div>

      {/* Progress Summary */}
      <div className="bg-light-surface dark:bg-surface rounded-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <span className="text-3xl font-bold text-accent">{completionPercentage}%</span>
            <span className="text-light-text-secondary dark:text-foreground-secondary ml-2">complete</span>
          </div>
          <div className="flex gap-6 text-sm">
            <div>
              <span className="text-green-500 font-semibold">{completedItems}</span>
              <span className="text-light-text-secondary dark:text-foreground-tertiary ml-1">completed</span>
            </div>
            <div>
              <span className="text-yellow-500 font-semibold">{inProgressItems}</span>
              <span className="text-light-text-secondary dark:text-foreground-tertiary ml-1">in progress</span>
            </div>
            <div>
              <span className="text-light-text-secondary dark:text-foreground-tertiary font-semibold">
                {totalItems - completedItems - inProgressItems}
              </span>
              <span className="text-light-text-secondary dark:text-foreground-tertiary ml-1">not started</span>
            </div>
          </div>
        </div>
        <div className="h-3 bg-background rounded-full overflow-hidden">
          <div
            className="h-full bg-green-500 transition-all"
            style={{ width: `${completionPercentage}%` }}
          />
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-4">
        <div>
          <label className="block text-sm text-light-text-secondary dark:text-foreground-secondary mb-1">Category</label>
          <select
            value={filterCategory}
            onChange={(e) => setFilterCategory(e.target.value)}
            className="p-2 bg-light-surface dark:bg-surface border border-light-border dark:border-border rounded text-light-text dark:text-foreground"
          >
            <option value="all">All Categories</option>
            {categories.map((cat) => (
              <option key={cat} value={cat}>
                {cat.replace(/-/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-sm text-light-text-secondary dark:text-foreground-secondary mb-1">Status</label>
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="p-2 bg-light-surface dark:bg-surface border border-light-border dark:border-border rounded text-light-text dark:text-foreground"
          >
            <option value="all">All Statuses</option>
            <option value="not-started">Not Started</option>
            <option value="in-progress">In Progress</option>
            <option value="completed">Completed</option>
            <option value="not-applicable">N/A</option>
          </select>
        </div>
      </div>

      {/* Requirements & Checklists */}
      <div className="space-y-6">
        {filteredRequirements.map((requirement) => (
          <RequirementCard
            key={requirement.id}
            requirement={requirement}
            checklistState={checklistState}
            onUpdateStatus={updateItemStatus}
          />
        ))}
      </div>

      {filteredRequirements.length === 0 && (
        <div className="text-center py-12 text-light-text-secondary dark:text-foreground-tertiary">
          No requirements match the current filters.
        </div>
      )}
    </div>
  );
}

function RequirementCard({
  requirement,
  checklistState,
  onUpdateStatus,
}: {
  requirement: ComplianceRequirement;
  checklistState: ChecklistState;
  onUpdateStatus: (itemId: string, status: ItemStatus) => void;
}) {
  const [expanded, setExpanded] = useState(true);

  const completedCount = requirement.checklistItems.filter(
    (item) => checklistState[item.id] === 'completed'
  ).length;

  const priorityColor =
    requirement.priority === 'critical'
      ? 'text-red-500'
      : requirement.priority === 'high'
      ? 'text-accent'
      : requirement.priority === 'medium'
      ? 'text-yellow-500'
      : 'text-green-500';

  return (
    <div className="bg-light-surface dark:bg-surface rounded-lg overflow-hidden">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full px-6 py-4 flex items-center justify-between hover:bg-light-hover dark:hover:bg-surface/70 transition-colors"
      >
        <div className="flex items-center gap-3">
          <span className={`text-xs font-semibold px-2 py-1 rounded ${priorityColor} bg-current/10`}>
            {requirement.priority.toUpperCase()}
          </span>
          <div className="text-left">
            <h3 className="font-semibold text-light-text dark:text-foreground">{requirement.title}</h3>
            <span className="text-sm text-light-text-secondary dark:text-foreground-tertiary">{requirement.article}</span>
          </div>
        </div>
        <div className="flex items-center gap-4">
          <span className="text-sm text-light-text-secondary dark:text-foreground-tertiary">
            {completedCount}/{requirement.checklistItems.length} completed
          </span>
          <svg
            className={`w-5 h-5 text-light-text-secondary dark:text-foreground-tertiary transition-transform ${expanded ? 'rotate-180' : ''}`}
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </div>
      </button>

      {expanded && (
        <div className="px-6 pb-4">
          <p className="text-sm text-light-text-secondary dark:text-foreground-secondary mb-4">{requirement.description}</p>
          <div className="space-y-3">
            {requirement.checklistItems.map((item) => (
              <ChecklistItemRow
                key={item.id}
                item={item}
                status={checklistState[item.id] || 'not-started'}
                onStatusChange={(status) => onUpdateStatus(item.id, status)}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function ChecklistItemRow({
  item,
  status,
  onStatusChange,
}: {
  item: ChecklistItem;
  status: ItemStatus;
  onStatusChange: (status: ItemStatus) => void;
}) {
  const statusColors: Record<ItemStatus, string> = {
    'not-started': 'bg-gray-500',
    'in-progress': 'bg-yellow-500',
    completed: 'bg-green-500',
    'not-applicable': 'bg-gray-400',
  };

  return (
    <div className="flex items-start gap-3 p-3 bg-background rounded-lg">
      <select
        value={status}
        onChange={(e) => onStatusChange(e.target.value as ItemStatus)}
        className={`p-1 rounded text-xs font-semibold text-white ${statusColors[status]}`}
      >
        <option value="not-started">Not Started</option>
        <option value="in-progress">In Progress</option>
        <option value="completed">Completed</option>
        <option value="not-applicable">N/A</option>
      </select>
      <div className="flex-1">
        <p className={`text-sm ${status === 'completed' ? 'text-light-text-secondary dark:text-foreground-tertiary line-through' : 'text-light-text dark:text-foreground'}`}>
          {item.text}
        </p>
        {item.description && (
          <p className="text-xs text-light-text-secondary dark:text-foreground-tertiary mt-1">{item.description}</p>
        )}
      </div>
    </div>
  );
}
